import React, { useState, useEffect } from 'react';
import { User, Mail, Building, FileText, Key, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { getAllAvailableCourses, CourseOption } from '@/hooks/auth/enhancedCourseService';
import { StudentFormData } from './StudentDetailsStep';

interface AccountSummaryStepProps {
  studentData: StudentFormData;
  selectedCourses: string[];
  onEdit: (step: 'courses' | 'details') => void;
  isCreating: boolean;
  sendWelcomeEmail: boolean;
  onSendWelcomeEmailChange: (value: boolean) => void;
  creationResult?: {
    success: boolean;
    temporaryPassword?: string;
    message?: string;
  };
}

const AccountSummaryStep: React.FC<AccountSummaryStepProps> = ({
  studentData,
  selectedCourses,
  onEdit,
  isCreating,
  sendWelcomeEmail,
  onSendWelcomeEmailChange,
  creationResult
}) => {
  const [selectedCourseDetails, setSelectedCourseDetails] = useState<CourseOption[]>([]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const courses = await getAllAvailableCourses();
        const details = selectedCourses
          .map(courseId => courses.find(course => course.id === courseId))
          .filter(Boolean) as CourseOption[];
        setSelectedCourseDetails(details);
      } catch (error) {
        console.error('Failed to load courses:', error);
      }
    };
    
    loadCourses();
  }, [selectedCourses]);

  const getCategoryColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'react': return 'bg-blue-100 text-blue-800';
      case 'sql': return 'bg-purple-100 text-purple-800';
      case 'python': return 'bg-green-100 text-green-800';
      case 'ai': return 'bg-orange-100 text-orange-800';
      case 'devops': return 'bg-indigo-100 text-indigo-800';
      case 'appsec': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (creationResult) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Account Creation Result</h2>
        </div>

        {creationResult.success ? (
          <div className="space-y-6">
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Student account has been created successfully! The student can now log in using their email and temporary password.
              </AlertDescription>
            </Alert>

            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800 flex items-center">
                  <Key className="mr-2 h-5 w-5" />
                  Login Credentials
                </CardTitle>
                <CardDescription className="text-green-700">
                  Share these credentials with the student securely.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-green-800">Email Address</label>
                    <div className="p-3 bg-white rounded-lg border border-green-200 font-mono text-sm">
                      {studentData.email}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-green-800">Temporary Password</label>
                    <div className="p-3 bg-white rounded-lg border border-green-200 font-mono text-sm">
                      {creationResult.temporaryPassword}
                    </div>
                  </div>
                </div>
                
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Important:</strong> The student must change their password on first login. 
                    The temporary password will expire in 48 hours for security.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Student Information Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                    <p className="mt-1">{studentData.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="mt-1">{studentData.email}</p>
                  </div>
                  {studentData.department && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Department</label>
                      <p className="mt-1">{studentData.department}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Assigned Courses</label>
                    <p className="mt-1">{selectedCourseDetails.length} courses</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                The student will be guided through a password change and course overview on their first login.
              </p>
            </div>
          </div>
        ) : (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to create student account: {creationResult.message}
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold">Review & Create Account</h2>
        <p className="text-muted-foreground mt-2">
          Please review all information before creating the student account.
        </p>
      </div>

      {/* Student Information Review */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <User className="mr-2 h-5 w-5" />
                Student Information
              </CardTitle>
              <CardDescription>
                Basic details for the new student account.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit('details')}>
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Full Name</p>
                <p className="text-sm text-muted-foreground">{studentData.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Email Address</p>
                <p className="text-sm text-muted-foreground">{studentData.email}</p>
              </div>
            </div>
            
            {studentData.department && (
              <div className="flex items-center space-x-3">
                <Building className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Department</p>
                  <p className="text-sm text-muted-foreground">{studentData.department}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center space-x-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Account Type</p>
                <p className="text-sm text-muted-foreground">Student</p>
              </div>
            </div>
          </div>
          
          {studentData.notes && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium">Additional Notes</p>
                <p className="text-sm text-muted-foreground mt-1">{studentData.notes}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Course Assignments Review */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Course Assignments ({selectedCourseDetails.length})
              </CardTitle>
              <CardDescription>
                Courses that will be automatically assigned to this student.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit('courses')}>
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {selectedCourseDetails.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                No courses selected. You can assign courses later from the user management table.
              </p>
            </div>
          )}
          {selectedCourseDetails.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedCourseDetails.map(course => (
                <div key={course.id} className="p-4 border rounded-lg">
                  <h4 className="font-medium text-sm">{course.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {course.description || 'No description available'}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Badge className={getCategoryColor(course.type)} variant="secondary">
                      {course.type?.toUpperCase() || 'GENERAL'}
                    </Badge>
                    <Badge variant="outline">
                      {course.level || 'Intermediate'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-sm font-medium">No courses selected</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                The student will not have any courses assigned initially.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Creation Info */}
      <Alert>
        <Key className="h-4 w-4" />
        <AlertDescription>
          <strong>What happens next:</strong>
          <ul className="mt-2 space-y-1 text-sm">
            <li>• A student account will be created with a secure temporary password</li>
            <li>• Selected courses will be automatically assigned</li>
            <li>• The student will be required to change their password on first login</li>
            <li>• They will see a welcome screen with their assigned courses</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Welcome Email Option */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="sendWelcomeEmail"
              checked={sendWelcomeEmail}
              onCheckedChange={(checked) => onSendWelcomeEmailChange(!!checked)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="sendWelcomeEmail"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Send welcome email with credentials
              </Label>
              <p className="text-sm text-muted-foreground">
                The student will receive an email with their login credentials and course information.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountSummaryStep;